import { memo, useCallback, useEffect, useRef } from 'react';

import { type JobSummaryViewModel, getJobIdentifier, useJobImprovedSelection } from '../../model';
import type { JobImprovedSelectProps } from './JobImprovedSelect.types';
import { JobImprovedSelectView } from './JobImprovedSelectView';

export const JobImprovedSelect = memo((props: JobImprovedSelectProps) => {
  const {
    type,
    searchPlaceholder,
    enabled = true,
    selectedJob: initialSelectedJob,
    onSelectionChange,
    onEditJob,
  } = props;

  const onAfterAutoProcessAts = useCallback(
    (job: JobSummaryViewModel) => {
      onSelectionChange?.(job);
    },
    [onSelectionChange],
  );

  const {
    search,
    selectedJob,
    jobList,
    isJobListLoading,
    isImproving,
    onSearchChange,
    onSelectJob: hookOnSelectJob,
  } = useJobImprovedSelection({ enabled, type, onAfterAutoProcessAts });

  const onSelectJob = useCallback(
    (job: JobSummaryViewModel | null) => {
      hookOnSelectJob(job);
      onSelectionChange?.(job);
    },
    [hookOnSelectJob, onSelectionChange],
  );

  const selectedJobRef = useRef(selectedJob);
  selectedJobRef.current = selectedJob;

  const hookOnSelectJobRef = useRef(hookOnSelectJob);
  hookOnSelectJobRef.current = hookOnSelectJob;

  const externalSyncKey =
    initialSelectedJob === undefined
      ? undefined
      : initialSelectedJob === null
        ? 'none'
        : `${getJobIdentifier(initialSelectedJob)}|${initialSelectedJob.type}`;

  useEffect(() => {
    if (externalSyncKey === undefined) return;

    const internal = selectedJobRef.current;

    if (
      internal &&
      initialSelectedJob &&
      initialSelectedJob.type === 'ATS_RAW' &&
      internal.type !== 'ATS_RAW' &&
      internal.jobAtsId === initialSelectedJob.jobAtsId
    ) {
      return;
    }

    const internalKey =
      internal === null || internal === undefined ? 'none' : `${getJobIdentifier(internal)}|${internal.type}`;

    if (internalKey === externalSyncKey) return;

    hookOnSelectJobRef.current(initialSelectedJob ?? null);
  }, [externalSyncKey, initialSelectedJob]);

  return (
    <JobImprovedSelectView
      search={search}
      onSearchChange={onSearchChange}
      selectedJob={selectedJob}
      onSelectJob={onSelectJob}
      jobList={jobList}
      isJobListLoading={isJobListLoading || isImproving}
      searchPlaceholder={searchPlaceholder}
      onEditJob={onEditJob}
    />
  );
});

JobImprovedSelect.displayName = 'JobImprovedSelect';
